// Side-effect import: <std-dsh-turn-row> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type.
import "./row";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import { DshTurnRow } from "./row";
import { TurnDiff } from "../thread/diff";

/** `tool`, in the dsh register — one row, and the call's output in a card
 * behind it.
 *
 * The row is `std-dsh-turn-row` and says exactly what every other row in
 * this skin says: a glyph for the KIND of call, a label for what happened, a
 * subject for what it happened to, and a trailing fact. What is particular
 * to a tool call is underneath.
 *
 * ## Three bodies, and the text decides which
 *
 * A unified diff is the one output whose meaning is carried by the first
 * character of every line, so `detail.language === "diff"` gets a classified
 * pass: signs stripped and redrawn in a gutter the selection never sees,
 * hunk headers drawn as gaps rather than as content, and totals underneath.
 * That pass is ported from the native skin, which took it from dsh in the
 * first place — the same grammar, the same state machine, a different cap.
 *
 * Everything else gets a CARD: a bordered box with an `IN` section and an
 * `OUT` section on one grid. That is dsh's shape and it is not decoration.
 * A shell call is a question and an answer, and a body that shows only the
 * answer makes the reader scroll back to the row to find what was asked —
 * which is exactly what happens when the row's subject has been replaced by
 * an error message, the one case where this skin renders both.
 *
 * ## The constituents are NOT behind the chevron
 *
 * `actions[]` renders as sub-rows in this component's own root, beneath the
 * row and outside its disclosure. The contract is explicit about why: a
 * surface folds the OUTPUT and keeps the call, and a constituent is part of
 * the call. `/bin/zsh -lc "sed -n '1,240p' AGENTS.md"` is what the reader
 * cannot parse; `Read AGENTS.md` is what they came for, and folding it hides
 * the only readable thing the row has.
 *
 * ## Four lines a side, not eight
 *
 * The native skin caps a long body at 16 lines and shows 8 at each end. This
 * one caps at 8 and shows 4, which is dsh's in-chat figure. The difference is
 * the surface: dsh's transcript is a chat column where a body is something
 * you glance at on the way past, and 16 lines of cargo output in a chat is a
 * page. Both ends, not just the head, for the reason the native note gives —
 * a `tail -4` of a build is the error and a `head -4` is the banner, and a
 * body truncated at one end always throws away one of them. */
@component({ name: "std-dsh-turn-tool" })
export class DshTurnTool extends LonaWebComponent {
  private $row: DshTurnRow = this.hydrate("row");
  private $actions: HTMLElement = this.$("actions");

  /** Past this many lines a body is capped head-and-tail; each end shows
   * CAP_EDGE of them. Two constants rather than one and a division, so a
   * revision wanting a longer preview than half the cap can say so. */
  static readonly CAP_LINES = 8;
  static readonly CAP_EDGE = 4;

  static makeWith(props: DshTurnTool.Props): DshTurnTool {
    return (DshTurnTool.make() as DshTurnTool).bind(props);
  }

  /** Every field is written on every bind and nothing is accumulated: the
   * row rebuilds its whole tree, the action column is `replaceChildren`d,
   * and the body's cap state is a closure created fresh by `buildBody`. A
   * pooled row that the reader had expanded comes back collapsed and capped,
   * holding the new call's lines. */
  bind(props: DshTurnTool.Props): this {
    this.setAttribute("status", props.status);
    const subject = DshTurnTool.subject(props);
    void this.$row.bind({
      kind: DshTurnTool.kind(props),
      label: props.verb ?? props.tool,
      subject,
      suffix: DshTurnTool.suffix(props),
      state: DshTurnTool.STATE[props.status],
      body: DshTurnTool.buildBody(props, subject),
      bodyIndent: props.detail?.language === "diff" ? "prose" : "card",
      expanded: props.expanded ?? false,
      // The subject is the command the body is the output OF, so it stays on
      // the row while the body is open. Scrolled to the bottom of a 40-line
      // build log, "what was this" is the reader's question.
      keepSubjectWhenOpen: true,
    });
    this.$actions.replaceChildren(
      ...(props.actions ?? []).map((action) => DshTurnTool.buildAction(action)),
    );
    return this;
  }

  /** What the row's gutter says this call IS.
   *
   * A heuristic over the dialect's own words with a DEFAULT, not a closed
   * vocabulary: a producer that invents a tenth call type gets the generic
   * glyph and a correct row, which is the property that lets both skins
   * survive a vendor change without either one being edited. The four
   * special cases are the four that make up the overwhelming majority of
   * every corpus sampled on this host. */
  private static kind(props: DshTurnTool.Props): DshTurnRow.Kind {
    const word = `${props.verb ?? ""} ${props.tool}`.toLowerCase();
    if (/search|grep|find|glob|lookup/.test(word)) return "search";
    if (/read|explor|list|cat|open/.test(word)) return "read";
    if (/edit|updat|writ|patch|apply|creat/.test(word)) return "edit";
    return "tool";
  }

  private static readonly STATE: Record<DshTurnTool.Status, DshTurnRow.State> = {
    pending: "running",
    running: "running",
    done: "idle",
    failed: "error",
    declined: "warn",
    // `pending` and `running` are one state to a reader waiting on a row:
    // the only question the gutter answers is whether a verdict has landed.
  };

  /** What the row is ABOUT.
   *
   * Normally the title. On a failure it is the first line of the output, in
   * red, replacing it — because at that moment the reader's question has
   * changed. `cargo build -p tento-spool` is what they already know; `error:
   * unresolved import` is what they came back for, and making them open a
   * disclosure to see it is making them ask a question the row could have
   * answered. The command is not lost: it becomes the card's `IN`. */
  private static subject(props: DshTurnTool.Props): string | HTMLElement {
    if (props.status !== "failed") return props.title;
    const first = DshTurnTool.firstLine(props.detail?.text ?? "");
    if (first === "") return props.title;
    const $subject = Typography.small(first);
    Object.assign($subject.style, {
      marginTop: "0",
      marginBottom: "0",
      color: "var(--red, #fe887c)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    });
    return $subject;
  }

  /** The trailing fact. `note` when the dialect supplied one — and otherwise
   * a ONE-LINE output, which has nowhere else to go: a single line is not a
   * body (a disclosure over one line is ceremony, the verdict the native
   * skin collected too) and dropping it would lose the only thing the call
   * returned. It never displaces a note; it only fills the gap. */
  private static suffix(props: DshTurnTool.Props): Option<string> {
    const note = props.note ?? "";
    if (note !== "") return note;
    const text = (props.detail?.text ?? "").replace(/\s+$/, "");
    return text === "" || text.includes("\n") ? null : text;
  }

  private static firstLine(text: string): string {
    return text.split("\n").find((line) => line.trim() !== "")?.trim() ?? "";
  }

  // ---- the bodies -----------------------------------------------------

  /** The disclosed body, or null for a row that does not open.
   *
   * The test is the LINE, not the length: a short multi-line output still
   * discloses, because its SHAPE is what makes it worth reading apart — a
   * diff, a stack, a table — and a 4,000-character single line is one line
   * and rides the row. */
  private static buildBody(
    props: DshTurnTool.Props,
    subject: string | HTMLElement,
  ): Option<HTMLElement> {
    const text = (props.detail?.text ?? "").replace(/\s+$/, "");
    if (!text.includes("\n")) return null;
    if (props.detail?.language === "diff") return DshTurnTool.buildDiff(text);
    // `IN` earns its column only when the row is no longer showing the
    // command — which is the failure case, where the subject was replaced.
    // Otherwise the command is six pixels above the card and printing it
    // again is a gutter label pointing at a duplicate.
    const command = typeof subject === "string" ? null : props.title;
    return DshTurnTool.buildCard(command, text);
  }

  /** The IN/OUT card.
   *
   * One CSS grid and not two stacked blocks: `max-content 1fr` is what puts
   * `IN` and `OUT` on one left edge whatever their widths, so the two
   * payloads share a text column and the card reads as one object with two
   * parts rather than as two boxes that happen to touch. The divider between
   * them spans the whole grid, which is the only thing in the card that
   * crosses the gutter.
   *
   * Each section scrolls on its OWN max-height. A card with one scroll for
   * both makes a long `IN` push `OUT` out of view, and `OUT` is what the
   * reader opened the card for.
   *
   * The rules travel as a `<style>` inside the card, because the card is
   * appended into the accordion's shadow root two boundaries away and a
   * sheet declared in this component's root cannot reach it. */
  private static buildCard(command: Option<string>, out: string): HTMLElement {
    const $card = document.createElement("div");
    $card.className = command == null ? "dsh-card dsh-card-bare" : "dsh-card";

    const $style = document.createElement("style");
    $style.textContent = DshTurnTool.CARD_STYLE;
    $card.append($style);

    if (command != null) {
      $card.append(
        DshTurnTool.gutter("IN"),
        DshTurnTool.payload(command),
        DshTurnTool.cardDivider(),
        DshTurnTool.gutter("OUT"),
      );
    }
    $card.append(DshTurnTool.payload(out));
    return $card;
  }

  /** The literal word, one gray step dimmer than the payload it labels. Text
   * and not an icon: `IN` and `OUT` are two characters wider than any glyph
   * that would have to be explained, and the column they sit in is
   * `max-content`, so they cost exactly their own width. */
  private static gutter(label: string): HTMLElement {
    const $gutter = Typography.small(label);
    $gutter.className = "small dsh-gutter";
    return $gutter;
  }

  private static payload(text: string): HTMLElement {
    const $payload = document.createElement("pre");
    $payload.className = "code-block dsh-payload";
    $payload.textContent = text;
    return $payload;
  }

  private static cardDivider(): HTMLElement {
    const $divider = document.createElement("div");
    $divider.className = "dsh-card-divider";
    $divider.setAttribute("aria-hidden", "true");
    return $divider;
  }

  /** One constituent, as a sub-row under the call rather than a row of its
   * own. `verb subject` in the row's own two gray steps, with a rule down
   * the left saying which call these belong to — the same claim nesting
   * makes everywhere, said once for the whole batch instead of once per
   * line. Not a disclosure: there is nothing under a constituent to open. */
  private static buildAction(action: DshTurnTool.Action): HTMLElement {
    const $line = document.createElement("div");
    $line.className = "action";

    const $verb = Typography.small(action.verb);
    $verb.className = "small action-verb";

    const $subject = Typography.small(action.subject);
    $subject.className = "small action-subject";

    $line.append($verb, $subject);
    return $line;
  }

  // ---- the patch ------------------------------------------------------

  /** What each line of a unified diff IS — `TurnDiff.classify`, the shared
   * semantic pass beside the slot contract. This skin keeps the STRIPPED
   * body (the sign is redrawn by `::before`, so a reader who selects the
   * body gets the code, not code with a column of `+` welded to it) and
   * maps kinds to its own gutter, cap and footer below. */
  private static classifyDiff(lines: string[]): DshTurnTool.DiffLine[] {
    return TurnDiff.classify(lines).map(({ kind, text }) => ({ kind, text }));
  }

  /** The patch, its stylesheet, its cap and its footer as one block.
   *
   * Classified ONCE, from the whole text, and only then capped. A line's kind
   * is not a property of the line — ` -x` is a removal inside a hunk and a
   * filename beside `---` outside one — so the pass that decides needs every
   * line before it in hand. Classifying a capped slice reads the tail's
   * context as file chrome.
   *
   * `full` is per-BUILD state, and that is what makes the cap idempotent
   * under pooling: a rebind rebuilds this element from scratch, so a body the
   * reader expanded and the pool then handed to another call comes back
   * capped rather than showing the previous call's full output. */
  private static buildDiff(text: string): HTMLElement {
    const rows = DshTurnTool.classifyDiff(text.split("\n"));

    const $wrap = document.createElement("div");
    $wrap.className = "dsh-diff";
    $wrap.style.setProperty("--font-size", "var(--turn-fine-size, var(--text-8px))");
    $wrap.style.setProperty("--line-height-ratio", "1.55");

    const $style = document.createElement("style");
    $style.textContent = DshTurnTool.DIFF_STYLE;

    const $body = document.createElement("pre");
    $body.className = "code-block dsh-diff-body";

    let full = false;
    const onToggle = (): void => {
      full = !full;
      render();
    };
    const render = (): void => {
      $body.replaceChildren(...DshTurnTool.diffNodes(rows, full, onToggle));
    };
    render();

    const $foot = document.createElement("div");
    $foot.className = "code-block dsh-diff-foot";
    $foot.textContent = DshTurnTool.diffFooter(rows);

    // The style and the footer are children of the WRAPPER, not of the body,
    // so `render`'s replaceChildren cannot sweep either away on a cap
    // toggle — and the footer stays put while the body scrolls under it,
    // because a total that scrolls out of view is a total the reader has to
    // go looking for.
    $wrap.append($style, $body, $foot);
    return $wrap;
  }

  /** One row of a patch, as a BLOCK.
   *
   * Plain output is text with newlines in it; a patch is a stack of rows,
   * because a row is the thing that carries a sign column and a hunk break
   * is a row that carries neither. Blocks copy as lines in every engine
   * measured, so nothing is lost by the change of model — and what is gained
   * is that the sign lives in a pseudo-element the selection never sees. */
  private static diffRow(row: DshTurnTool.DiffLine): HTMLElement {
    const $row = document.createElement("div");
    $row.className = row.kind === "hunk" || row.kind === "meta"
      ? `diff-chrome diff-${row.kind}`
      : `diff-line diff-${row.kind}`;
    $row.textContent = row.text;
    return $row;
  }

  /** The cap, over a classified patch: head, a control, tail. The control is
   * a ROW of the patch rather than a run inside one — there are no newline
   * nodes here to splice it between. */
  private static diffNodes(
    rows: DshTurnTool.DiffLine[],
    full: boolean,
    onToggle: () => void,
  ): Node[] {
    const draw = (row: DshTurnTool.DiffLine): Node => DshTurnTool.diffRow(row);
    if (rows.length <= DshTurnTool.CAP_LINES) return rows.map(draw);
    if (full) {
      return [...rows.map(draw), DshTurnTool.capRow("… show less", onToggle)];
    }
    const hidden = rows.length - DshTurnTool.CAP_EDGE * 2;
    return [
      ...rows.slice(0, DshTurnTool.CAP_EDGE).map(draw),
      DshTurnTool.capRow(`… ${hidden} more lines`, onToggle),
      ...rows.slice(-DshTurnTool.CAP_EDGE).map(draw),
    ];
  }

  /** The cap's control. A real `<button>` rather than a clickable span: it is
   * the only interactive thing inside a body that is otherwise verbatim text,
   * so it has to be reachable by keyboard, and every UA gives a button that
   * for free. Everything that would make it look like a button is stripped —
   * inside a patch it is a line of the output. */
  private static capRow(label: string, onToggle: () => void): HTMLElement {
    const $row = document.createElement("div");
    $row.className = "diff-chrome";
    const $toggle = document.createElement("button");
    $toggle.type = "button";
    $toggle.className = "cap-toggle";
    $toggle.textContent = label;
    $toggle.addEventListener("click", (event) => {
      // The body sits inside the accordion's content region rather than its
      // header, so nothing above listens for this today — but a click that
      // bubbled into a header would collapse the row the reader is reading,
      // and that is not a bug worth discovering later.
      event.stopPropagation();
      onToggle();
    });
    $row.append($toggle);
    return $row;
  }

  /** `└ +A −R · N files`, counted from the WHOLE patch.
   *
   * Never from the capped slice: the footer's only job is to say what the
   * body cannot show, and a total that shrank when the body folded would be
   * the one number on screen that lies.
   *
   * Counted from the CLASSIFIED rows and not the raw lines — whether `+++ x`
   * is a file header or an added line that happens to open with plus signs
   * is decided by where it sits relative to a hunk, and `classifyDiff`
   * already decided that. These totals match `git diff --stat` precisely
   * because they count what the gutter shows. The file count comes from
   * `diff --git` headers and falls back to `+++` ones, and is dropped
   * entirely at one file, where the row above already names the path. */
  private static diffFooter(rows: DshTurnTool.DiffLine[]): string {
    let added = 0;
    let removed = 0;
    let git = 0;
    let plus = 0;
    for (const row of rows) {
      if (row.kind === "add") added += 1;
      else if (row.kind === "del") removed += 1;
      else if (row.kind !== "meta") continue;
      else if (row.text.startsWith("diff --git ")) git += 1;
      else if (row.text.startsWith("+++")) plus += 1;
    }
    const files = git > 0 ? git : plus;
    const counts = `└ +${added} −${removed}`;
    return files > 1 ? `${counts} · ${files} files` : counts;
  }

  /** The patch's own rules, carried into the accordion's shadow root by the
   * `<style>` `buildDiff` appends.
   *
   * An array of quoted lines rather than a tagged template, and it is the
   * one thing in this file that is not a taste decision: a stray backtick
   * inside a css`` literal terminates it and ships a blank page. This text is
   * edited by hand.
   *
   * Selectors that touch a Typography element are DESCENDED from another
   * class. Component sheets are pushed onto `adoptedStyleSheets`, which the
   * cascade orders AFTER tree `<style>` elements, so a bare class in here
   * loses the tie to Typography's own rule and its cap-height margin trim
   * survives into a flex or grid box. Two classes beat one. See the same
   * note on `DshTurnRow.ROW_STYLE`.
   *
   * A hunk header is NOT body text. It is the one line of a patch that says
   * nothing about the file and everything about the patch's own bookkeeping,
   * so it draws as a rule with a whisper on it — a gap between two runs of
   * code, which is what a reader actually uses it as. */
  private static readonly DIFF_STYLE = [
    ".diff-line { display: block; }",
    ".diff-line::before { display: inline-block; width: 1ch; }",
    ".diff-add { color: var(--green, #51b748); }",
    '.diff-add::before { content: "+"; }',
    ".diff-del { color: var(--red, #fe887c); }",
    '.diff-del::before { content: "-"; }',
    ".diff-ctx { color: var(--text-sec); }",
    '.diff-ctx::before { content: " "; }',
    ".diff-chrome { display: block; }",
    /* An empty inline box, so a blank chrome line still holds a line box
       instead of collapsing to nothing. */
    '.diff-chrome::before { content: ""; display: inline-block; }',
    ".diff-meta { color: var(--text-ter); opacity: 0.7; }",
    ".diff-hunk {",
    "  color: var(--text-ter);",
    "  opacity: 0.55;",
    "  font-size: 0.85em;",
    "  border-top: 1px solid var(--divider, rgba(128, 128, 128, 0.28));",
    "  margin-top: 5px;",
    "  padding-top: 4px;",
    "}",
    ".diff-hunk:first-child {",
    "  border-top: none;",
    "  margin-top: 0;",
    "  padding-top: 0;",
    "}",
    ".dsh-diff .dsh-diff-body {",
    "  margin: 0;",
    "  padding: 0;",
    "  color: var(--text-sec);",
    "  white-space: pre;",
    "  overflow-x: auto;",
    "}",
    ".dsh-diff .dsh-diff-foot {",
    "  margin: 3px 0 0 0;",
    "  color: var(--text-ter);",
    "  white-space: pre;",
    "  opacity: 0.85;",
    "}",
    ".dsh-diff .cap-toggle {",
    "  display: inline;",
    "  margin: 0;",
    "  padding: 0;",
    "  border: none;",
    "  background: none;",
    "  font: inherit;",
    "  color: var(--text-ter);",
    "  cursor: pointer;",
    "  text-align: left;",
    "}",
    ".dsh-diff .cap-toggle:hover { color: var(--text-sec); }",
  ].join("\n");

  /** The card's rules, carried the same way and quoted for the same reason.
   *
   * `position: sticky` on the gutter labels rather than a plain block: the
   * card is short enough today that nothing scrolls past them, and it will
   * not stay that way — a caller that grows the section heights gets labels
   * that stay beside their payload rather than scrolling off the top of it,
   * for one declaration. */
  private static readonly CARD_STYLE = [
    ".dsh-card {",
    "  display: grid;",
    "  grid-template-columns: max-content minmax(0, 1fr);",
    "  column-gap: 14px;",
    "  row-gap: 4px;",
    "  align-items: start;",
    "  box-sizing: border-box;",
    "  padding: 8px 10px;",
    "  border: 1px solid var(--divider, rgba(127, 127, 127, 0.28));",
    "  border-radius: 12px;",
    "  background: var(--bg-hover, color-mix(in srgb, var(--text-pri, #1f2328) 4%, transparent));",
    "  --font-size: var(--turn-fine-size, var(--text-8px));",
    "  --line-height-ratio: 1.55;",
    "}",
    /* No gutter, no column: an OUT-only card is one payload and printing an
       empty max-content track beside it indents the output for nothing. */
    ".dsh-card-bare { grid-template-columns: minmax(0, 1fr); }",
    ".dsh-card .dsh-gutter {",
    "  position: sticky;",
    "  top: 0;",
    "  margin: 0;",
    "  min-height: 0;",
    "  color: var(--text-ter, #8b93a7);",
    "  opacity: 0.7;",
    "  white-space: nowrap;",
    "}",
    ".dsh-card .dsh-payload {",
    "  margin: 0;",
    "  padding: 0;",
    "  min-width: 0;",
    "  max-height: 150px;",
    "  color: var(--text-sec);",
    "  white-space: pre;",
    "  overflow: auto;",
    "}",
    ".dsh-card .dsh-card-divider {",
    "  grid-column: 1 / -1;",
    "  height: 1px;",
    "  margin: 3px 0;",
    "  background: var(--divider, rgba(127, 127, 127, 0.28));",
    "}",
  ].join("\n");

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      /* Aligned under the row's SUBJECT, not under its gutter: the
         constituents continue the sentence the row started, and starting
         them at the glyph column would read as a second column of calls.
         22px is the lead box plus its gap, the same figure the prose body
         indents by, so a batch and an output land on one edge.

         The rule is what says these belong to the row above. It needs
         something on both sides of it to earn its ink and here it has
         that — rows to its right, a row above — which is exactly the test
         the native skin's disclosed bodies FAILED, where the same rule was
         removed on review. */
      #actions:not(:empty) {
        margin: 1px 0 2px 22px;
        padding-left: 8px;
        border-left: 1px solid var(--divider, rgba(127, 127, 127, 0.28));
      }
      .action {
        display: flex;
        align-items: baseline;
        gap: 6px;
        min-width: 0;
        min-height: 18px;
      }
      /* The row's own two gray steps, and no third. A constituent is a row
         of this transcript written smaller, not a different kind of thing. */
      .action-verb {
        flex: none;
        margin: 0;
        color: var(--text-sec, #6a7186);
        white-space: nowrap;
      }
      .action-subject {
        min-width: 0;
        margin: 0;
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-dsh-turn-row id="row"></std-dsh-turn-row>
      <div id="actions"></div>
    </div>
  `;
}

export namespace DshTurnTool {
  export type Status = "pending" | "running" | "done" | "failed" | "declined";

  /** One constituent of a batched call. Structural twin of
   * `TurnSlots.ToolAction`, declared here rather than imported for the reason
   * every prop in a skin is: the skin describes itself, and the contract
   * asserts the match at compile time. */
  export type Action = { verb: string; subject: string };

  /** The five things a line of a unified diff can be, once `classifyDiff` has
   * decided. `meta` is every kind of chrome at once — `diff --git`, `index`,
   * the `---`/`+++` pair, a bare path, a binary notice — because this skin
   * draws all of them the same way, and a finer union would be a vocabulary
   * to keep in step with every producer that ever writes a patch. */
  export type DiffKind = "add" | "del" | "ctx" | "hunk" | "meta";

  /** A classified line. `text` is the line with its SIGN REMOVED for
   * add/del/ctx — the sign is redrawn by `::before` — and verbatim for
   * `hunk` and `meta`, which have no sign to strip. */
  export type DiffLine = { kind: DiffKind; text: string };

  export type Props = {
    /** The tool's own name — "read", "execute", "web_search". Used as the
     * row's one word when the dialect supplied no `verb`. */
    tool: string;
    /** What the tool DID, in the dialect's own words. Open text: the
     * translator owns the wording. Wins over `tool`; the row shows one word,
     * never both. */
    verb?: string;
    /** The subject: a path, a query, a command, a prompt. */
    title: string;
    /** A trailing fact the row earned — "6 sources", "161 lines", "exit 0". */
    note?: string;
    /** What one batched call actually did. Rendered as sub-rows outside the
     * disclosure; empty or absent for an ordinary call. */
    actions?: Action[];
    /** The call's output, with an optional hint about what the text IS. The
     * hint is about the text and never about its box. */
    detail?: {
      text: string;
      language?: "plain" | "lisp" | "js" | "ts" | "rust" | "diff";
    };
    expanded?: boolean;
    status: Status;
  };
}
